import { useCallback, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Flame, Hash, UserPlus, Users } from 'lucide-react'
import { useGroup, useSteamFriends } from '../hooks/useGroups'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { useGroupPool } from '../hooks/useGroupPool'
import { useGiftables } from '../hooks/useGiftables'
import { useGiftPurchases } from '../hooks/useGiftPurchases'
import { useAuth } from '../contexts/AuthContext'
import { GroupChat } from '../components/chat/GroupChat'
import { GiftableSection } from '../components/group/GiftableSection'
import { GiftPurchaseCard } from '../components/group/GiftPurchaseCard'
import { PoolPanel } from '../components/group/PoolPanel'
import { Avatar, Badge, Button, Card, Dialog, Input, Spinner } from '../components/ui'
import { formatPrice, libraryBadge } from '../lib/format'
import type { Giftable, SteamFriend } from '../lib/types'

// Human hint under a friend's name: how much they play with you + presence. The
// order the server returns already reflects this; the hint just explains it.
function friendHint(friend: SteamFriend): string | null {
  const parts: string[] = []
  if (friend.overlap_score != null && friend.overlap_score > 0) parts.push('Plays with you')
  if (friend.online) parts.push('Online')
  return parts.length ? parts.join(' · ') : null
}

export function GroupDetailScreen({ groupId }: { groupId: string }) {
  const { group, isLoading, error, addMember } = useGroup(groupId)
  // Fallbacks mirror the visible h1 ({group.name ?? 'Group'}) so the document
  // title and heading never disagree for unnamed groups.
  usePageTitle(group?.name ?? 'Group', LEGACY_PRODUCT_NAME)
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [steamId, setSteamId] = useState('')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState('')
  // Lazily load the signed-in user's ordered Steam friends only while the dialog
  // is open; pass groupId so already-added friends come back flagged.
  const { friends, status: friendsStatus, isLoading: friendsLoading } = useSteamFriends(dialogOpen, groupId)
  const [addingFriendId, setAddingFriendId] = useState<string | null>(null)
  // Friends added in-session (before the picker refetches) show as done too.
  const [addedFriendIds, setAddedFriendIds] = useState<Set<string>>(new Set())

  // ── Wallet / pool / gifting (§3.7) — every surface below is gated on the
  // server feature flag; with it off the hooks stay idle and the screen
  // renders exactly what it did before the feature existed.
  const { features, user } = useAuth()
  const walletOn = features.group_wallet
  const {
    pool,
    walletBalanceCents,
    busy: poolBusy,
    mutationError: poolError,
    refresh: refreshPool,
    contribute,
    withdraw,
  } = useGroupPool(groupId, walletOn)
  const { giftables, readiness, refresh: refreshGiftables } = useGiftables(groupId, walletOn)
  // Any purchase mutation can move pool money (a vote may fund, transferring
  // the price to the recipient's wallet) or flip a giftable row to "View
  // proposal" — one callback re-syncs both sibling surfaces.
  const handleGiftMutated = useCallback(() => {
    void refreshPool()
    void refreshGiftables()
  }, [refreshPool, refreshGiftables])
  const {
    purchases,
    propose,
    vote,
    cancel,
    proposing,
    mutatingId,
    mutationError: giftError,
  } = useGiftPurchases(groupId, walletOn, handleGiftMutated)

  const [proposeTarget, setProposeTarget] = useState<Giftable | null>(null)
  // v1 giftables always carry exactly one missing member — the gift recipient.
  const proposeRecipient = proposeTarget?.missing_members[0] ?? null
  const purchaseRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const poolCardRef = useRef<HTMLDivElement>(null)

  // "View proposal" on a giftable anchors to its purchase card in the list.
  const scrollToPurchase = (purchaseId: string) => {
    purchaseRefs.current.get(purchaseId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  // "Pool is short — chip in" on a card anchors to the pool panel (it has no
  // externally-openable chip-in dialog; scrolling is the honest wiring).
  const scrollToPool = () => {
    poolCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const confirmPropose = async () => {
    if (!proposeTarget || !proposeRecipient) return
    try {
      await propose(proposeTarget.appid, proposeRecipient.user_id)
      setProposeTarget(null)
    } catch {
      // useGiftPurchases surfaces friendly copy via giftError; keep the
      // confirm dialog open so the member can retry or back out.
    }
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!steamId.trim()) return
    setAdding(true)
    setAddError('')
    try {
      await addMember(steamId.trim())
      setDialogOpen(false)
      setSteamId('')
    } catch {
      setAddError('Could not add that Steam ID. Check it and try again.')
    } finally {
      setAdding(false)
    }
  }

  const handleAddFriend = async (friend: SteamFriend) => {
    setAddingFriendId(friend.steam_id)
    setAddError('')
    try {
      await addMember(friend.steam_id)
      setAddedFriendIds((prev) => new Set(prev).add(friend.steam_id))
    } catch {
      setAddError(`Could not add ${friend.persona_name ?? 'that friend'}. Try again.`)
    } finally {
      setAddingFriendId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !group) {
    return <p className="text-sm text-danger-fg">{error ?? 'Group not found.'}</p>
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
      <div className="space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl leading-tight text-text-strong">{group.name ?? 'Group'}</h1>
            <p className="mt-1 text-sm text-text-muted">
              {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          {/* flex-wrap: actions stack whole at tight widths — labels never
              wrap mid-word (Button is nowrap) and never clip. */}
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setDialogOpen(true)}>
              <UserPlus className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
              Add friend
            </Button>
            <Button
              onClick={() => navigate({ to: '/steam/groups/$groupId/play', params: { groupId } })}
              disabled={group.members.length === 0}
            >
              <Flame className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
              Play tonight
            </Button>
          </div>
        </header>

        <Card>
          <h2 className="mb-3 text-xl font-semibold text-text-strong">Members</h2>
          {group.members.length === 0 ? (
            <p className="text-sm text-text-muted">No members yet. Add friends from your Steam list or by Steam ID.</p>
          ) : (
            <ul className="space-y-3">
              {group.members.map((m) => {
                const badge = libraryBadge(m.library_status, m.owned_game_count)
                return (
                  <li key={m.user_id} className="flex items-center gap-3">
                    <Avatar name={m.display_name} src={m.avatar_url} size="sm" />
                    <span className="text-sm text-text">{m.display_name ?? 'Steam friend'}</span>
                    {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
                  </li>
                )
              })}
            </ul>
          )}
        </Card>

        {walletOn && purchases.length > 0 && (
          <section aria-labelledby="gift-proposals-heading" className="space-y-4">
            <h2 id="gift-proposals-heading" className="text-xl font-semibold text-text-strong">
              Gift proposals
            </h2>
            {giftError && proposeTarget === null && (
              <p className="text-sm text-danger-fg">{giftError}</p>
            )}
            {purchases.map((purchase) => (
              <div
                key={purchase.id}
                ref={(el) => {
                  if (el) purchaseRefs.current.set(purchase.id, el)
                  else purchaseRefs.current.delete(purchase.id)
                }}
              >
                <GiftPurchaseCard
                  purchase={purchase}
                  members={group.members}
                  viewerUserId={user?.id}
                  busy={mutatingId === purchase.id}
                  onVote={(choice) => void vote(purchase.id, choice)}
                  onCancel={() => void cancel(purchase.id)}
                  onChipIn={scrollToPool}
                />
              </div>
            ))}
          </section>
        )}

        {walletOn && readiness && (
          <GiftableSection
            giftables={giftables}
            readiness={readiness}
            walletFlagOn
            onPropose={(giftable) => setProposeTarget(giftable)}
            onViewProposal={scrollToPurchase}
          />
        )}

        {walletOn && pool && (
          <div ref={poolCardRef}>
            <PoolPanel
              pool={pool}
              walletBalanceCents={walletBalanceCents}
              onContribute={contribute}
              onWithdraw={withdraw}
              busy={poolBusy}
              error={poolError}
              onGoToWallet={() => navigate({ to: '/steam/wallet' })}
            />
          </div>
        )}
      </div>

      <GroupChat groupId={groupId} members={group.members} />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} title="Add a friend">
        <div className="space-y-4">
          <section className="space-y-3 rounded-lg border border-border-warm p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-ember-300" strokeWidth={1.75} aria-hidden="true" />
              <h3 className="text-sm font-semibold text-text-strong">From your Steam friends</h3>
            </div>
            <p className="text-xs text-text-muted">Pick people already on your Steam friends list.</p>
            {friendsLoading ? (
              <div className="flex justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : friendsStatus === 'private' ? (
              <p className="text-sm text-text-muted">
                Your Steam friends list is private, so we can&rsquo;t list it here. Add friends by their Steam ID below.
              </p>
            ) : friends.length === 0 ? (
              <p className="text-sm text-text-muted">No Steam friends found. Add by Steam ID below.</p>
            ) : (
              <ul className="max-h-64 space-y-2 overflow-y-auto">
                {friends.map((f) => {
                  const done = f.already_member || addedFriendIds.has(f.steam_id)
                  const hint = friendHint(f)
                  return (
                    <li key={f.steam_id} className="flex items-center justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-2">
                        <Avatar name={f.persona_name} src={f.avatar_url} size="sm" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-text">{f.persona_name ?? f.steam_id}</span>
                          {hint && <span className="block truncate text-xs text-text-muted">{hint}</span>}
                        </span>
                      </span>
                      {done ? (
                        <Badge variant="success">Added</Badge>
                      ) : (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          loading={addingFriendId === f.steam_id}
                          onClick={() => handleAddFriend(f)}
                        >
                          Add
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section className="space-y-3 rounded-lg border border-border-warm p-4">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-text-muted" strokeWidth={1.75} aria-hidden="true" />
              <h3 className="text-sm font-semibold text-text-strong">Add by Steam ID</h3>
            </div>
            <p className="text-xs text-text-muted">
              For someone who isn&rsquo;t on your friends list — paste their 17-digit Steam ID.
            </p>
            <form onSubmit={handleAdd} className="space-y-4">
              <Input
                label="Steam ID (steamID64)"
                value={steamId}
                onChange={(e) => setSteamId(e.target.value)}
                placeholder="7656119…"
                helpText="Their public 17-digit Steam ID."
                error={addError}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={adding}>
                  Add by ID
                </Button>
              </div>
            </form>
          </section>
        </div>
      </Dialog>

      {walletOn && (
        <Dialog
          open={proposeTarget !== null}
          onClose={() => setProposeTarget(null)}
          title="Propose a gift"
        >
          {proposeTarget && proposeRecipient && (
            <div className="space-y-4">
              <p className="text-sm text-text">
                Gift{' '}
                <span className="font-semibold text-text-strong">
                  {proposeTarget.name ?? `App ${proposeTarget.appid}`}
                </span>{' '}
                to{' '}
                <span className="font-semibold text-text-strong">
                  {proposeRecipient.display_name ?? 'the missing member'}
                </span>
                {proposeTarget.price && <> for {formatPrice(proposeTarget.price)}</>}? The group
                votes, and the pool pays for it once a majority approves.
              </p>
              {giftError && <p className="text-sm text-danger-fg">{giftError}</p>}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setProposeTarget(null)}>
                  Cancel
                </Button>
                <Button type="button" loading={proposing} onClick={() => void confirmPropose()}>
                  Propose gift
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      )}
    </div>
  )
}
